/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    LinkFieldValue,
    MetaDescription,
    MetaKeywords,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import Page from "@insite/mobius/Page";
import * as React from "react";

const enum fields {
    header = "header",
    imageUrl = "imageUrl",
    link = "link",
    copy = "copy",
    openInNewTab = "openInNewTab",
}

interface AllProductPageProps extends PageProps {
    fields: {
        [fields.header]: string;
        [fields.imageUrl]: string;
        [fields.link]: LinkFieldValue;
        [fields.copy]: string;
        [fields.openInNewTab]: boolean;
    };
}

type AllProductProps = AllProductPageProps & PageProps;

const AllProducts: React.FC<AllProductProps> = ({ id }) => {
    return (
        <Page>
            <Zone contentId={id} zoneName="Content"></Zone>
            <AddToListModal />
        </Page>
    );
};

const allProductsPageModule = {
    component: AllProducts,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                name: fields.header,
                displayName: "Promotion Header",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                validate: (value: string | null) => {
                    if (!value || value.length === 0) {
                        return "Header is required";
                    }
                    return null;
                },
            },
            {
                name: fields.imageUrl,
                displayName: "Promotion Image",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                validate: (value: string | null) => {
                    if (!value || value.length === 0) {
                        return "Image is required";
                    }
                    return null;
                },
            },
            {
                name: fields.link,
                displayName: "Promotion Link",
                editorTemplate: "CustomLinkField",
                defaultValue: { value: "", type: "Page" },
                fieldType: "General",
                isRequired: true,
                validate: (link: LinkFieldValue) => {
                    if (!link || !link.value) {
                        return "A value must be provided.";
                    }
                    return null;
                },
            },
            {
                name: fields.copy,
                displayName: "Promotion Content",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                validate: (value: string | null) => {
                    if (value && value.length > 400) {
                        return "Max length is 400 characters";
                    }
                    return null;
                },
            },
            {
                name: fields.openInNewTab,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default allProductsPageModule;

export const AllProductsPageContext = "AllProducts";
